import { CONTENT_SET_TEMPLATE_PREFIX } from "../template-chart-config"
import type { SensitivityType } from "../factor-sensitivity/types"

export type CustomGraphFamily = "exposure" | "content-set" | "risk" | "factor-sensitivity" | "performance"

export const CUSTOM_GRAPH_FAMILY_LABELS: Record<CustomGraphFamily, string> = {
  exposure: "Exposure",
  "content-set": "Content Sets",
  risk: "Risk",
  "factor-sensitivity": "Factor Sensitivity",
  performance: "Performance",
}

// Derived from CUSTOM_GRAPH_FAMILY_LABELS so a new family can't be added there without being routed here.
export const CUSTOM_GRAPH_FAMILIES = Object.keys(CUSTOM_GRAPH_FAMILY_LABELS) as CustomGraphFamily[]

export type CustomGraphFamilyContext =
  | { family: "exposure" }
  | { family: "risk" }
  | { family: "performance" }
  | { family: "factor-sensitivity", sensitivityType: SensitivityType }
  | { family: "content-set", contentSetId: string }

export function getCustomGraphFamilyGroupId(family: CustomGraphFamily): string {
  return `custom-${family}`
}

export function getCustomGraphFamilyGroupLabel(family: CustomGraphFamily): string {
  return `Custom ${CUSTOM_GRAPH_FAMILY_LABELS[family]}`
}

export function sortCustomTemplatesByFamily<T extends { id: string }>(
  nameSortedTemplates: T[],
  familyContextsById: Record<string, CustomGraphFamilyContext | null | undefined>,
): T[] {
  const familyBuckets = new Map<CustomGraphFamily, T[]>(CUSTOM_GRAPH_FAMILIES.map(family => [family, []]))
  const unresolvedBucket: T[] = []
  for (const template of nameSortedTemplates) {
    const family = familyContextsById[template.id]?.family
    const bucket = (family && familyBuckets.get(family)) || unresolvedBucket
    bucket.push(template)
  }
  return [...CUSTOM_GRAPH_FAMILIES.flatMap(family => familyBuckets.get(family) ?? []), ...unresolvedBucket]
}

function familyMetricPrefix(context: CustomGraphFamilyContext): string {
  switch (context.family) {
    case "content-set":
      // encodeURIComponent escapes ":" so a contentSetId containing one can't be mistaken for the delimiter.
      return `content-set:${encodeURIComponent(context.contentSetId)}:`
    case "factor-sensitivity":
      return `factor-sensitivity:${context.sensitivityType}:`
    default:
      return `${context.family}:`
  }
}

export function encodeFamilyMetricIds(context: CustomGraphFamilyContext, ids: string[]): string[] {
  const prefix = familyMetricPrefix(context)
  if (ids.length === 0) return [prefix]
  return ids.map(id => `${prefix}${id}`)
}

const CONTENT_SET_METRIC_PATTERN = /^content-set:([^:]*):/
const FACTOR_SENSITIVITY_METRIC_PATTERN = /^factor-sensitivity:(historical|predicted):/

export interface DecodedFamilyMetrics {
  context: CustomGraphFamilyContext | null
  ids: string[]
}

export function decodeFamilyMetricIds(metrics: string[] | null | undefined): DecodedFamilyMetrics {
  const ids = metrics ?? []
  const first = ids[0]
  if (!first) return { context: null, ids }

  const contentSetMatch = first.match(CONTENT_SET_METRIC_PATTERN)
  if (contentSetMatch) {
    return stripPrefix(ids, contentSetMatch[0], { family: "content-set", contentSetId: decodeURIComponent(contentSetMatch[1]) })
  }

  const factorSensitivityMatch = first.match(FACTOR_SENSITIVITY_METRIC_PATTERN)
  if (factorSensitivityMatch) {
    return stripPrefix(ids, factorSensitivityMatch[0], { family: "factor-sensitivity", sensitivityType: factorSensitivityMatch[1] as SensitivityType })
  }

  for (const family of ["exposure", "risk", "performance"] as const) {
    const prefix = `${family}:`
    if (first.startsWith(prefix)) {
      return stripPrefix(ids, prefix, { family })
    }
  }

  return { context: null, ids }
}

function stripPrefix(ids: string[], prefix: string, context: CustomGraphFamilyContext): DecodedFamilyMetrics {
  return { context, ids: ids.map(id => (id.startsWith(prefix) ? id.slice(prefix.length) : id)).filter(isRealMetricId) }
}

function isRealMetricId(id: string): boolean {
  return id !== ""
}

const FACTOR_SENSITIVITY_VIEW_IDS: Record<string, SensitivityType> = {
  "factor-sensitivity-historical": "historical",
  "factor-sensitivity-predicted": "predicted",
}

export const CATEGORIES_WITHOUT_SOURCEABLE_METRICS: Partial<Record<string, string[]>> = {
  performance: ["total", "factors"],
  risk: ["total"],
}

export function isCategoryEligibleForDuplicate(viewId: string | null | undefined, selectedCategory: string): boolean {
  const excludedCategories = viewId && Object.hasOwn(CATEGORIES_WITHOUT_SOURCEABLE_METRICS, viewId)
    ? CATEGORIES_WITHOUT_SOURCEABLE_METRICS[viewId]
    : undefined
  return !(excludedCategories ?? []).includes(selectedCategory)
}

export function resolveFamilyContextFromBuiltInViewId(viewId: string | null | undefined): CustomGraphFamilyContext | null {
  if (!viewId) return null
  if (viewId === "exposure") return { family: "exposure" }
  if (viewId === "risk") return { family: "risk" }
  if (viewId === "performance") return { family: "performance" }
  if (Object.hasOwn(FACTOR_SENSITIVITY_VIEW_IDS, viewId)) {
    return { family: "factor-sensitivity", sensitivityType: FACTOR_SENSITIVITY_VIEW_IDS[viewId] }
  }
  if (viewId.startsWith(CONTENT_SET_TEMPLATE_PREFIX)) {
    return { family: "content-set", contentSetId: viewId.slice(CONTENT_SET_TEMPLATE_PREFIX.length) }
  }
  return null
}

interface CustomGraphFamilyContextInputs {
  decodedTemplateMetrics: DecodedFamilyMetrics
  duplicateFromTemplateId: string | null | undefined
  decodedDuplicateSourceMetrics: DecodedFamilyMetrics
  duplicateReturnViewId: string | null | undefined
}

// wrapper.tsx and header.tsx must both resolve family through here, or they can render mismatched families.
export function resolveCustomGraphFamilyContext({
  decodedTemplateMetrics,
  duplicateFromTemplateId,
  decodedDuplicateSourceMetrics,
  duplicateReturnViewId,
}: CustomGraphFamilyContextInputs): CustomGraphFamilyContext | null {
  if (decodedTemplateMetrics.context) return decodedTemplateMetrics.context
  if (duplicateFromTemplateId) return decodedDuplicateSourceMetrics.context
  return resolveFamilyContextFromBuiltInViewId(duplicateReturnViewId)
}
